/**
 * Access-decision events, sent through the logAccessEvent callable (one
 * structured Cloud Logging line each). Fire-and-forget: an analytics failure
 * must never surface to the person or break the page emitting it.
 */
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "../store/firebaseApp";
import {
  ACCESS_EVENT_ALLOWED_PROPS,
  type AccessEventName,
  type PrivateProgrammeDeniedViewProps,
} from "@lemonadetraining/contracts";

type AccessEventProps = {
  private_programme_denied_view: PrivateProgrammeDeniedViewProps;
};

type EventProps = Record<string, string | number | boolean>;

/** Drops any property not in the event's allowlist, so PII can't ride along. */
function allowedProps(name: AccessEventName, props: EventProps): EventProps {
  const allowlist: readonly string[] = ACCESS_EVENT_ALLOWED_PROPS[name] ?? [];
  const filtered: EventProps = {};
  for (const key of allowlist) {
    if (props[key] !== undefined) filtered[key] = props[key];
  }
  return filtered;
}

export function trackAccessEvent<N extends AccessEventName>(
  name: N,
  props: AccessEventProps[N]
): void {
  try {
    const logAccessEvent = httpsCallable(firebaseFunctions, "logAccessEvent");
    void logAccessEvent({ name, props: allowedProps(name, props as EventProps) }).catch(() => {
      // Swallowed on purpose: an analytics failure must never surface.
    });
  } catch {
    // Analytics must never break the calling flow.
  }
}
